import { FC } from "react";
import { Box, Chip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import formatDateTime from "../helpers/formatDateTime";
import { filterConditionsList } from "../modules/dashboardModule/components/DashboardFiltersTab";

type RenderFilterChipsProps = {
  data: any;
  isSeprate?: boolean;
  isAdvanced?: boolean;
  isCloseable?: boolean;
  onChipDelete?: (key: string, value: string | number, index?: number) => void;
};

enum CONFIG_TYPES {
  ARRAY = "array",
  DATERANGE = "dateRange",
  SINGLE = "single",
  ADVANCEFILTERS = "advanceFilters",
}

const CHIP_CONFIG: Record<
  string,
  {
    label: string;
    type:
      | CONFIG_TYPES.ARRAY
      | CONFIG_TYPES.DATERANGE
      | CONFIG_TYPES.SINGLE
      | CONFIG_TYPES.ADVANCEFILTERS;
  }
> = {
  otNames: { label: "OT", type: CONFIG_TYPES.ARRAY },
  procedureIds: { label: "Primary Procedure", type: CONFIG_TYPES.ARRAY },
  primarySurgeons: { label: "Primary Surgeon", type: CONFIG_TYPES.ARRAY },
  attendingSurgeonNames: { label: "Primary Surgeon", type: CONFIG_TYPES.ARRAY },
  circulatingNurses: { label: "Circulating Nurse", type: CONFIG_TYPES.ARRAY },
  anesthesiologists: { label: "Anesthesiologist", type: CONFIG_TYPES.ARRAY },
  caseStartDelays: { label: "Case Start Delay", type: CONFIG_TYPES.ARRAY },
  dateRange: { label: "Date", type: CONFIG_TYPES.DATERANGE },
  caseOrder: { label: "Case Order", type: CONFIG_TYPES.SINGLE },
  caseIds: { label: "Case IDs", type: CONFIG_TYPES.ARRAY },
  autoClosed: { label: "Auto Closed", type: CONFIG_TYPES.SINGLE },
  antibioticUsed: {
    label: "Antibiotic Used",
    type: CONFIG_TYPES.SINGLE,
  },
  inconsistentTimestamps: {
    label: "Inconsistent Timestamps",
    type: CONFIG_TYPES.SINGLE,
  },
  advanceFilters: {
    label: "Advanced Filters",
    type: CONFIG_TYPES.ADVANCEFILTERS,
  },
};

const chipStyle = {
  mr: 0.5,
  mb: 0.5,
  borderRadius: "14px",
  background: "#E5E5E5",
  fontSize: "13px",
  px: "8px",
  maxWidth: 360,
  "& .MuiChip-label": {
    maxWidth: "100%",
  },
  "& .MuiChip-deleteIcon": {
    opacity: 0,
    transition: "opacity 0.2s",
  },
  "&:hover .MuiChip-deleteIcon": {
    opacity: 1,
  },
};

const getConditionLabel = (operator: string) =>
  filterConditionsList.find((cond) => cond?.key === operator)?.value ?? "";

const normalizeText = (v: any) => String(v ?? "").trim();

const toUtcDateOnly = (input: any): Date | null => {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
};

const RenderFilterChips: FC<RenderFilterChipsProps> = ({
  data,
  isSeprate = false,
  isAdvanced = false,
  isCloseable = false,
  onChipDelete,
}) => {
  const renderChip = (
    key: string,
    label: string,
    text: string,
    index?: number,
    deleteCb?: () => void
  ) => {
    const full = normalizeText(text);

    return (
      <Chip
        key={`${key}-${index ?? "single"}`}
        label={
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: "100%",
            }}
          >
            <Box component="strong" sx={{ whiteSpace: "nowrap" }}>
              {label}
            </Box>

            <Box
              component="span"
              sx={{
                display: "inline-block",
                maxWidth: 240,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={full} // ✅ native browser tooltip only (no MUI Tooltip)
            >
              {full}
            </Box>
          </Box>
        }
        onDelete={isCloseable && deleteCb ? deleteCb : undefined}
        deleteIcon={isCloseable && deleteCb ? <CloseIcon /> : undefined}
        sx={chipStyle}
      />
    );
  };

  const renderArrayChips = (key: string, label: string, values: string[]) => {
    if (!Array.isArray(values) || values.length === 0) return [];

    return values.map((item: string, index: number) =>
      renderChip(
        key,
        `${label}:`,
        item,
        index,
        onChipDelete ? () => onChipDelete(key, item, index) : undefined
      )
    );
  };

  const renderDateChip = (key: string, label: string, value: any) => {
    const start = value?.startDate ?? value?.from;
    const end = value?.endDate ?? value?.to;
    if (!start || !end) return [];

    const startDate = toUtcDateOnly(start);
    const endDate = toUtcDateOnly(end);
    if (!startDate || !endDate) return [];

    const text = `${formatDateTime(startDate, false, "dd-MMM yyyy")} - ${formatDateTime(endDate, false, "dd-MMM yyyy")}`;


    return [renderChip(key, label, text)];
  };

  const renderSingleChip = (key: string, label: string, value: any) => {
    if (value === undefined || value === null || value === "") return [];

    return [
      renderChip(
        key,
        `${label}:`,
        String(value),
        undefined,
        onChipDelete ? () => onChipDelete(key, value) : undefined
      ),
    ];
  };

  // ✅ Issue 2: break down advanced rendering (no deep nesting)
  const buildAdvancedChipItems = (filters: any[]) => {
    if (!Array.isArray(filters) || filters.length === 0) return [];

    const items: Array<{
      chipKey: string;
      label: string;
      text: string;
      filterIndex: number;
      rawValue: string | number;
    }> = [];

    filters.forEach((filter: any, filterIndex: number) => {
      const fieldLabel =
        CHIP_CONFIG[filter?.filterType]?.label ?? "Advanced Filters";
      const conditionLabel = getConditionLabel(filter?.condition);

     let selected: any[] = [];

if (Array.isArray(filter?.selectedValues)) {
  selected = filter.selectedValues;
} else if (filter?.selectedValues != null) {
  selected = [filter.selectedValues];
}

      selected.forEach((sv: any) => {
        const v = sv?.name ?? sv;
        items.push({
          chipKey: "advanceFilters",
          label: fieldLabel,
          text: `${conditionLabel} ${v}`,
          filterIndex,
          rawValue: v,
        });
      });
    });

    return items;
  };

  const renderAdvancedFilterChips = (filters: any[]) => {
    const items = buildAdvancedChipItems(filters);

    return items.map((it, idx) =>
      renderChip(
        it.chipKey,
        it.label,
        it.text,
        idx,
        onChipDelete
          ? () => onChipDelete(it.chipKey, it.rawValue, it.filterIndex)
          : undefined
      )
    );
  };

  const shouldRenderSimple = (!isAdvanced && isSeprate) || !isSeprate;

  const wrapAdvancedGroup = (key: string, label: string, items: any[]) => {
    if (items.length === 0) return [];
    return [
      <Box key={key} sx={{ width: "100%", mt: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <strong>{label}:</strong>
        </Typography>
        {items}
      </Box>,
    ];
  };

  const handleDateRangeEntry = (key: string, label: string, value: any) => {
    return renderDateChip(key, label, value);
  };

  const handleAdvancedFiltersEntry = (
    key: string,
    label: string,
    value: any,
    isSeprate: boolean,
    shouldRenderSimple: boolean
  ) => {
    if (!Array.isArray(value)) return null;

    const items = renderAdvancedFilterChips(value);

    if (!isSeprate) return wrapAdvancedGroup(key, label, items);

    return shouldRenderSimple ? [] : items;
  };

  const handleSimpleEntry = (
    key: string,
    config: (typeof CHIP_CONFIG)[string],
    value: any,
    shouldRenderSimple: boolean
  ) => {
    if (!shouldRenderSimple) return [];

    if (config.type === CONFIG_TYPES.ARRAY && Array.isArray(value)) {
      return renderArrayChips(key, config.label, value);
    }

    if (config.type === CONFIG_TYPES.SINGLE) {
      return renderSingleChip(key, config.label, value);
    }

    return [];
  };

  const renderEntry = (key: string, value: any) => {
    const config = CHIP_CONFIG[key];
    if (!config || value === undefined || value === null) return null;

    switch (config.type) {
      case CONFIG_TYPES.DATERANGE:
        return handleDateRangeEntry(key, config.label, value);

      case CONFIG_TYPES.ADVANCEFILTERS:
        return handleAdvancedFiltersEntry(
          key,
          config.label,
          value,
          isSeprate,
          shouldRenderSimple
        );

      default:
        return handleSimpleEntry(key, config, value, shouldRenderSimple);
    }
  };

  const renderChips = (obj: Record<string, any>) => {
    const out: any[] = [];

    Object.keys(CHIP_CONFIG).forEach((key) => {
      const chunk = renderEntry(key, obj?.[key]);
      if (!chunk) return;
      out.push(...chunk);
    });

    return out;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        p: 1,
        gap: 1,
      }}
    >
      {renderChips(data)}
    </Box>
  );
};

export default RenderFilterChips;
